import React, { useState, useRef, useEffect } from 'react';
import uPlot from 'uplot';
import 'uplot/dist/uPlot.min.css';

const SignalPlotter = ({ unitId, selectedProject, selectedSession, currentVideoTime, loadingDbcInfo, canIds, signalsByCanId, sessionDuration, selectedDbcFile }) => {
  const [plots, setPlots] = useState([{ id: 0, signals: [{ canId: '', signal: '' }], signalData: [], loading: false, lastPlottedSignals: null }]);
  const plotRefs = useRef({});
  const chartInstances = useRef({});



  // Reset selections when project changes
  useEffect(() => {
    setPlots([{ id: 0, signals: [{ canId: '', signal: '' }], signalData: [], loading: false, lastPlottedSignals: null }]);
  }, [selectedProject]);

   // Update plot cursors when currentVideoTime changes
   useEffect(() => {
    plots.forEach(plot => {
      const chartInstance = chartInstances.current[plot.id];
      if (!chartInstance || !plot.signalData?.length) return;
      
      const rect = chartInstance.bbox;
      const xScale = chartInstance.scales.x;
      const leftPos = Math.round(xScale.distr === 2 ? xScale.pos(currentVideoTime) : (currentVideoTime - xScale.min) / (xScale.max - xScale.min) * rect.width);
      
      chartInstance.setCursor({ left: leftPos });
    });
  }, [currentVideoTime, plots]);
  useEffect(() => {
    plots.forEach(plot => {
      if (!plotRefs.current[plot.id]) return;
      
      if (chartInstances.current[plot.id]) {
        chartInstances.current[plot.id].destroy();
      }
      
      if (!plot.signalData || plot.signalData.length === 0) {
        const opts = {
          width: plotRefs.current[plot.id].offsetWidth,
          height: 200,
          scales: { x: { time: false, min: 0, max: sessionDuration || 1 }, y: { min: 0, max: 100 } },
          series: [{
            label: 'Time (mm:ss)',
            value: (u, v) => {
              const mins = Math.floor(v / 60);
              const secs = Math.floor(v % 60);
              return `${mins}:${secs.toString().padStart(2, '0')}`;
            }},
            { label: 'Value', stroke: '#ccc' }],
          axes: [
            { 
              label: 'Time (mm:ss)',
              values: (u, vals) => vals.map(v => {
                const mins = Math.floor(v / 60);
                const secs = Math.floor(v % 60);
                return `${mins}:${secs.toString().padStart(2, '0')}`;
              }),
            },
            { label: 'Value' }
          ],
          cursor: { sync: { key: 'signal-plots', setSeries: true } }
        };
        chartInstances.current[plot.id] = new uPlot(opts, [[0, sessionDuration || 1], [null, null]], plotRefs.current[plot.id]);
        return;
      }
      
      const allTimestamps = plot.signalData.flatMap(sd => sd.dataPoints.map(d => d.timestamp));
      const uniqueTimestamps = [...new Set(allTimestamps)].sort((a, b) => a - b);
      
      const data = [uniqueTimestamps];
      const series = [
        {
          label: 'Time (mm:ss)',
          value: (u, v) => {
            const mins = Math.floor(v / 60);
            const secs = Math.floor(v % 60);
            return `${mins}:${secs.toString().padStart(2, '0')}`;
          }
        }
      ];
      
      const colors = ['#007bff', '#28a745', '#dc3545', '#ffc107', '#17a2b8'];
      
      plot.signalData.forEach((sd, idx) => {
        const values = uniqueTimestamps.map(t => {
          const point = sd.dataPoints.find(p => p.timestamp === t);
          return point ? point.value : null;
        });
        data.push(values);
        series.push({ label: sd.signalName, stroke: colors[idx % colors.length], width: 1 });
      });
      
      const opts = {
        width: plotRefs.current[plot.id].offsetWidth,
        height: 200,
        scales: { x: { time: false } },
        series,
        axes: [
          { 
            label: 'Time (mm:ss)',
            values: (u, vals) => vals.map(v => {
              const mins = Math.floor(v / 60);
              const secs = Math.floor(v % 60);
              return `${mins}:${secs.toString().padStart(2, '0')}`;
            }),
          },
          { label: 'Value' }
        ],
        cursor: {
          sync: {
            key: 'signal-plots',
            setSeries: true
          }
        }
      };
      
      chartInstances.current[plot.id] = new uPlot(opts, data, plotRefs.current[plot.id]);
    });
    
    return () => {
      Object.values(chartInstances.current).forEach(chart => chart?.destroy());
    };
  }, [plots]);

  const handleGeneratePlot = async (plotId) => {
    const plot = plots.find(p => p.id === plotId);
    const validSignals = plot.signals.filter(s => s.canId && s.signal);
    if (validSignals.length === 0) return;
    
    const signalsKey = JSON.stringify(validSignals.map(s => ({ canId: s.canId, signal: s.signal })));
    
    setPlots(prev => prev.map(p => p.id === plotId ? { ...p, loading: true, signalData: [] } : p));
    
    try {
      const signalDataPromises = validSignals.map(signal =>
        fetch(
          `${process.env.REACT_APP_BASE_URL}/sessions/signal-data/${unitId}/${selectedProject}/${selectedSession}/${encodeURIComponent(signal.canId)}/${encodeURIComponent(signal.signal)}${selectedDbcFile ? `?dbcFile=${encodeURIComponent(selectedDbcFile)}` : ''}`,
          { credentials: 'include' }
        ).then(res => res.ok ? res.json() : null)
      );
      
      const results = await Promise.all(signalDataPromises);
      const validResults = results.filter(r => r !== null);
      
      setPlots(prev => prev.map(p => p.id === plotId ? { ...p, signalData: validResults, loading: false, lastPlottedSignals: signalsKey } : p));
    } catch (error) {
      console.error('Error fetching signal data:', error);
      setPlots(prev => prev.map(p => p.id === plotId ? { ...p, loading: false } : p));
    }
  };

  useEffect(() => {
    return () => {
      Object.values(chartInstances.current).forEach(chart => chart?.destroy());
    };
  }, []);

  return (
    <div style={{
      background: '#f8f9fa',
      border: '1px solid #dee2e6',
      borderRadius: '4px',
      padding: '12px',
      marginTop: '16px',
      fontSize: '12px'
    }}>
      <h6 style={{ margin: '0 0 8px 0', fontSize: '13px', fontWeight: 'bold' }}>Signal Plotter</h6>
      
      {plots.map((plot, index) => (
        <div key={plot.id} style={{ marginBottom: '16px' }}>
          {plot.signals.map((sig, sigIndex) => (
            <div key={sigIndex} style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '8px' }}>
              <select
                value={sig.canId}
                onChange={(e) => {
                  setPlots(prev => prev.map(p => {
                    if (p.id !== plot.id) return p;
                    const newSignals = [...p.signals];
                    newSignals[sigIndex] = { canId: e.target.value, signal: '' };
                    return { ...p, signals: newSignals, lastPlottedSignals: null };
                  }));
                }}
                disabled={loadingDbcInfo}
                style={{
                  padding: '4px 8px',
                  fontSize: '11px',
                  borderRadius: '3px',
                  border: '1px solid #ccc',
                  width: '150px'
                }}
              >
                <option value="">{loadingDbcInfo ? 'Loading...' : 'Select CAN ID'}</option>
                {canIds.map(canId => (
                  <option key={canId.id} value={canId.id}>
                    {canId.id} - {canId.name}
                  </option>
                ))}
              </select>
              
              <select
                value={sig.signal}
                onChange={(e) => {
                  setPlots(prev => prev.map(p => {
                    if (p.id !== plot.id) return p;
                    const newSignals = [...p.signals];
                    newSignals[sigIndex] = { ...newSignals[sigIndex], signal: e.target.value };
                    return { ...p, signals: newSignals, lastPlottedSignals: null };
                  }));
                }}
                disabled={!sig.canId || (signalsByCanId[parseInt(sig.canId)] || []).length === 0}
                style={{
                  padding: '4px 8px',
                  fontSize: '11px',
                  borderRadius: '3px',
                  border: '1px solid #ccc',
                  width: '150px'
                }}
              >
                <option value="">Select Signal</option>
                {sig.canId && (signalsByCanId[parseInt(sig.canId)] || []).map(signal => (
                  <option key={signal} value={signal}>
                    {signal}
                  </option>
                ))}
              </select>
              {sigIndex > 0 && (
                <button
                  onClick={() => {
                    setPlots(prev => prev.map(p => {
                      if (p.id !== plot.id) return p;
                      return { ...p, signals: p.signals.filter((_, i) => i !== sigIndex), lastPlottedSignals: null };
                    }));
                  }}
                  style={{
                    padding: '0',
                    fontSize: '11px',
                    cursor: 'pointer',
                    border: 'none',
                    borderRadius: '50%',
                    background: 'transparent',
                    color: '#dc3545',
                    width: '24px',
                    height: '24px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <i className="fa fa-minus-circle" style={{ fontSize: '20px' }} title="Remove signal from plot"></i>
                </button>
              )}
              {sigIndex === plot.signals.length - 1 && (
                <button
                  onClick={() => {
                    setPlots(prev => prev.map(p => {
                      if (p.id !== plot.id) return p;
                      return { ...p, signals: [...p.signals, { canId: '', signal: '' }] };
                    }));
                  }}
                  style={{
                    padding: '0',
                    fontSize: '11px',
                    cursor: 'pointer',
                    border: 'none',
                    borderRadius: '50%',
                    background: 'transparent',
                    color: '#28a745',
                    width: '24px',
                    height: '24px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <i className="fa fa-plus-circle" style={{ fontSize: '20px' }} title="Add another signal to plot"></i>
                </button>
              )}
              {sigIndex === plot.signals.length - 1 && (
                <button
                onClick={() => handleGeneratePlot(plot.id)}
                disabled={plot.loading || !plot.signals.some(s => s.canId && s.signal) || (plot.lastPlottedSignals === JSON.stringify(plot.signals.filter(s => s.canId && s.signal).map(s => ({ canId: s.canId, signal: s.signal }))))}
                style={{
                  padding: '4px 12px',
                  fontSize: '11px',
                  cursor: (plot.loading || !plot.signals.some(s => s.canId && s.signal) || (plot.lastPlottedSignals === JSON.stringify(plot.signals.filter(s => s.canId && s.signal).map(s => ({ canId: s.canId, signal: s.signal }))))) ? 'not-allowed' : 'pointer',
                  border: '1px solid #007bff',
                  borderRadius: '3px',
                  background: '#007bff',
                  color: '#fff',
                  opacity: (plot.loading || !plot.signals.some(s => s.canId && s.signal) || (plot.lastPlottedSignals === JSON.stringify(plot.signals.filter(s => s.canId && s.signal).map(s => ({ canId: s.canId, signal: s.signal }))))) ? 0.5 : 1,
                  marginLeft: '8px'
                }}
              >
                {plot.loading ? 'Generating...' : 'Generate Plot'}
              </button>
            )}
            {index > 0 && sigIndex === plot.signals.length - 1 && (
              <button
                onClick={() => {
                  setPlots(prev => prev.filter(p => p.id !== plot.id));
                }}
                style={{
                  padding: '0',
                  fontSize: '11px',
                  cursor: 'pointer',
                  border: 'none',
                  borderRadius: '50%',
                  background: 'transparent',
                  color: '#dc3545',
                  width: '24px',
                  height: '24px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: '8px'
                }}
              >
                <i className="fa fa-minus-circle" style={{ fontSize: '20px' }} title="Remove plot"></i>
              </button>
            )}</div>
          ))}
          



          
          <div>
            {plot.signalData && plot.signalData.length > 0 && (
              <div style={{ fontSize: '11px', color: '#666', marginBottom: '4px' }}>
                {plot.signalData.map(sd => `${sd.signalName} (${sd.count} pts)`).join(', ')}
              </div>
            )}
            <div ref={el => plotRefs.current[plot.id] = el} style={{ width: '100%' }} />
          </div>
        </div>
      ))}
      
      <button
        onClick={() => setPlots(prev => [...prev, { id: Date.now(), signals: [{ canId: '', signal: '' }], signalData: [], loading: false, lastPlottedSignals: null }])}
        style={{
          padding: '4px 12px',
          fontSize: '11px',
          cursor: 'pointer',
          border: '1px solid #28a745',
          borderRadius: '3px',
          background: '#28a745',
          color: '#fff'
        }}
      >
        Add Plot
      </button>
    </div>
  );
};

export default SignalPlotter;